/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable jsdoc/check-param-names */
/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/require-param-description */
/* eslint-disable jsdoc/valid-types */
import { Injectable } from '@angular/core';
import { NavigationEnd, Router, Event } from '@angular/router';
import { debounceTime, filter } from 'rxjs/operators';
import { ANALYTICS_TRACKING_ID } from '../app.constants';
import { SiteSearchResult } from '../interfaces/site-search-result.interface';
import { AppService } from '../services/app.service';
import { ErrorMessage } from '../models/error-message.model';
import { UserInterface } from '../interfaces/userInterface';
import { UserManagementService } from '../services/user-management.service';
import { RequestUtil } from './request.util';

declare global {
    interface Window {
        gtag: (command: string, action: string, parameters?: Record<string, any>) => void;
    }
}
  
interface EventData {
    [key: string]: any;
    event_category?: string;
    event_label?: string;
    value?: number;
    user_id?: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class AnalyticsUtil {
    private gtag: (command: string, action: string, parameters?: Record<string, any>) => void;
    private isTracking: boolean;
    private doNotTrackPaths: string[] = [
        '/login',
        '/login/callback'
    ];
    private userInfo: UserInterface | null = null;
    private customMap: {[key: string]: string} = {
        'dimension1': 'product_id',
        'dimension2': 'query',
        'dimension3': 'url',
        'dimension4': 'company',
        'dimension5': 'dynamics_customer_id',
        'dimension6': 'enterprise_customer_id',
        'metric1': 'product_news_results',
        'metric2': 'product_documentation_results',
        'metric3': 'product_downloads_results',
        'metric4': 'general_news_results'
    };
    private eventQueue: Array<() => void> = [];


    constructor(
        private router: Router,
        private userManagementService: UserManagementService,
        private requestUtil: RequestUtil,
        private appService: AppService
    ) {}

    async init() {
        if (!window.gtag) {
            console.error('Google Analytics is not initialized. Ensure the script is loaded in index.html.');
        } else {
            console.log('Google Analytics initialized:', ANALYTICS_TRACKING_ID);
        }
        
        if (this.isTracking) {
            return;
        }
    
        this.gtag = window.gtag;
    
        try {
            // Fetch `userInfo` before proceeding with initialization
            const userInfo = await this.fetchUserInfo();
            if (userInfo) {
                this.updateAnalyticsConfig(userInfo.enterpriseCustomerId, userInfo.companyName);
                this.processEventQueue();
            }
    
            // Begin tracking after userInfo is resolved
            this.router.events.pipe(
                filter((event: Event) => 
                    event instanceof NavigationEnd &&
                    this.doNotTrackPaths.indexOf((event).urlAfterRedirects) === -1
                ),
                debounceTime(300)
            ).subscribe((event: NavigationEnd) => {
                console.log('NavigationEnd detected:', event.urlAfterRedirects);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.trackPageView(event.urlAfterRedirects);
            });
    
            this.isTracking = true; // Mark tracking as initialized
        } catch (error) {
            console.error('Failed to fetch user info for analytics:', error);
        }
    }
    
    async trackPageView(path: string) {
        console.log('Tracking page view for path:', path);

        const userInfo = await this.fetchUserInfo();

        if (userInfo) {
            this.gtag('config', ANALYTICS_TRACKING_ID, {
                'page_path': path,
                'custom_map': this.customMap,
                'enterprise_customer_id': userInfo.enterpriseCustomerId,
                'company': userInfo.companyName
            });
        } else {
            // Queue the event for later
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            this.eventQueue.push(() => this.trackPageView(path));
        }
    }
        
    /**
     * The trackEvent function sets the default event properties that must be present for correct event registration.
     * Additionally, the default event is extended by an eventData object to provide custom event data.
     * @param action: Name of the GA action (a predefined value like search or a custom value)
     * @param category: Name of the GA category (a predefeinde value like engagement or a custom value)
     * @param label: Name of the GA label (custom value)
     * @param value: Numeric value used for GA reporting
     * @param eventData: Any custom data that will be sent with the event object. 
     * Custom data must be mapped to custom dimensions and metrics
     * @param action
     * @param category
     * @param label
     * @param value
     * @param eventData
     */
    async trackEvent(action: string, category: string, label: string, value: number, eventData: EventData = {}) {
        if (!category || !label || typeof value === 'undefined') {
            console.error('Invalid GA event', category, label, value);
        }
        const userInfo = await this.fetchUserInfo();
        eventData.event_category = category;
        eventData.event_label = label;
        eventData.value = value;
        if (userInfo) {
            eventData.enterprise_customer_id = userInfo?.enterpriseCustomerId;
            eventData.company = userInfo?.companyName;
        }
        this.gtag('event', action, eventData);
    }
    
    async trackSiteSearch(query: string, productId: string, results: SiteSearchResult) {
        const userInfo = await this.fetchUserInfo();

        const totalResults = results.productNews + results.productDocumentation + results.productSoftwareDownloads + results.news;
        const eventData = {
            query,
            'product_id': productId,
            'dynamics_customer_id': userInfo?.dynamicsCustomerId,
            'enterprise_customer_id': userInfo?.enterpriseCustomerId,
            'company': userInfo?.companyName,
            'product_news_results': results.productNews,
            'product_documentation_results': results.productDocumentation,
            'product_downloads_results': results.productSoftwareDownloads,
            'knowledge_search_results': results.knowledgeSearch,
            'community_search_results': results.communitySearch,
            'general_news_results': results.news,
        };
        await this.trackEvent('search', 'engagement', 'site-search', totalResults, eventData);
    }
    
    async trackSiteSearchNavigate(label: string, query: string, productId: string, results: SiteSearchResult) {
        const userInfo = await this.fetchUserInfo();

        let value: number;
        switch (label) {
        case 'product-news':
            value = results.productNews;
            break;
        case 'product-downloads':
            value = results.productSoftwareDownloads;
            break;
        case 'product-documentation':
            value = results.productDocumentation;
            break;
        case 'knowledge-search':
            value = results.knowledgeSearch;
            break;
        case 'community-search':
            value = results.communitySearch;
            break;
        case 'general-news':
            value = results.news;
            break;
        }
        const eventData = {
            query,
            'product_id': productId,
            'dynamics_customer_id': userInfo?.dynamicsCustomerId,
            'enterprise_customer_id': userInfo?.enterpriseCustomerId,
            'company': userInfo?.companyName,
            'product_news_results': results.productNews,
            'product_documentation_results': results.productDocumentation,
            'product_downloads_results': results.productSoftwareDownloads,
            'knowledge_search_results': results.knowledgeSearch,
            'general_news_results': results.news,
        };
        await this.trackEvent('search-result', 'engagement', label, value, eventData);
    }
                            
    trackClickEvent(label: string, key: string) {
        void this.trackEvent('click', 'engagement', label, 1, {
            query: key
        });
    }
                            
    trackError(error: ErrorMessage) {
        this.gtag('event', 'exception', {
            'description': `${error.title || error.content} - ${window.location.pathname}`,
            'fatal': false
        });
    }
                            
    trackUser(user: {
        user_id?: string;
    }) {
        const userId = user.user_id;
        if (userId) {
            this.gtag('config', ANALYTICS_TRACKING_ID, {
                'user_id': user.user_id,
                'custom_map': this.customMap
            });
        }
    }
                            
    private fetchUserInfo(): Promise<UserInterface> {
        if (this.userInfo) {
            // console.log('Using cached user info:', this.userInfo);
            return Promise.resolve(this.userInfo); // Return cached userInfo
        }
        console.log('Fetching user info from service...');

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.requestUtil
            .request(this.userManagementService.getCurrentUserProfile())
            .toPromise()
            .then((userInfo: UserInterface) => {
                // console.log('User info fetched successfully:', userInfo);
                this.userInfo = userInfo; // Cache userInfo
                return userInfo;
            })
            .catch(error => {
                console.error('Error fetching user info:', error);
                return null; // Return null if fetching fails
            });
    }
    
    
    private updateAnalyticsConfig(enterpriseCustomerId: string, companyName: string) {
        this.gtag('config', ANALYTICS_TRACKING_ID, {
            'enterprise_customer_id': enterpriseCustomerId,
            'company': companyName,
            'custom_map': this.customMap
        });
    }
    // Process queued events once userInfo is available
    private processEventQueue() {
        while (this.eventQueue.length > 0) {
            const eventFn = this.eventQueue.shift();
            if (eventFn) {
                eventFn();
            }
        }
    }   
}
